function deriveApiBase() {
  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_API_BASE ?? 'http://localhost:8000';
  }
  return `https://${hostname}:8443`;
}
export const API_BASE = deriveApiBase();

// Utility for token management
export function setAuthToken(token) {
  localStorage.setItem("authToken", token);
}
export function getAuthToken() {
  return localStorage.getItem("authToken");
}
export function removeAuthToken() {
  localStorage.removeItem("authToken");
}

// Activity signaling (used for idle/refresh tracking)
let activityCallback = null;
export function setActivityCallback(cb) {
  activityCallback = cb;
}
function markActivity() {
  if (typeof activityCallback === "function") activityCallback();
}

function authHeaders() {
  const token = getAuthToken();
  return token ? { "Authorization": `Bearer ${token}` } : {};
}

function makeOptions({ method = "GET", body = null, auth = true } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (auth) Object.assign(headers, authHeaders());
  const opts = { method, headers };
  if (body) opts.body = JSON.stringify(body);
  return opts;
}

function messageFromParsedError(parsed) {
  const detail = parsed?.detail ?? parsed?.message ?? parsed?.error;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (typeof item?.msg === "string") return item.msg;
        if (typeof item?.message === "string") return item.message;
        return null;
      })
      .filter(Boolean)
      .join(", ");
  }
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    return JSON.stringify(detail);
  }
  return "";
}

export function extractApiErrorMessage(raw, fallback = "Request failed") {
  if (!raw) return fallback;
  const message = raw instanceof Error ? raw.message : String(raw);
  if (!message.trim()) return fallback;
  try {
    const parsed = JSON.parse(message);
    return messageFromParsedError(parsed) || fallback;
  } catch {
    return message;
  }
}

async function readApiErrorMessage(res) {
  const fallback = `${res.status} ${res.statusText || "Request failed"}`.trim();
  return extractApiErrorMessage(await res.text(), fallback);
}

async function apiCall(path, { method = "GET", body = null, auth = true } = {}) {
  markActivity();
  const res = await fetch(`${API_BASE}${path}`, makeOptions({ method, body, auth }));
  if (res.status === 401) {
    if (auth) {
      removeAuthToken();
      window.location.replace("/login");
      return;
    }
    throw new Error(await readApiErrorMessage(res));
  }
  if (!res.ok) throw new Error(await readApiErrorMessage(res));
  // Handle 204 No Content responses (e.g., DELETE operations)
  if (res.status === 204) return null;
  return await res.json();
}

// Refresh token using existing bearer token; returns new token string or null if unauthorized
export async function refreshAccessToken() {
  markActivity();
  const res = await fetch(`${API_BASE}/token/refresh`, makeOptions({ method: "POST", auth: true }));
  if (res.status === 401) {
    removeAuthToken();
    window.location.replace("/login");
    return null;
  }
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  const token = data?.access_token;
  if (!token) {
    throw new Error("Token refresh response missing access_token field");
  }
  return token;
}


/**
 * Download an artifact file (image/pdf/etc) from the backend with authentication.
 * Returns the raw Response object for blob handling.
 */
export async function downloadArtifactFile(agentId, version, runNumber, fileName) {
  markActivity();
  const token = getAuthToken();
  const url = `${API_BASE}/agent/${agentId}/version/${version}/runs/${runNumber}/artifact/${encodeURIComponent(fileName)}`;
  const headers = token ? { "Authorization": `Bearer ${token}` } : {};
  const response = await fetch(url, { method: "GET", headers });
  if (response.status === 401) {
    removeAuthToken();
    window.location.replace("/login");
    return;
  }
  return response; // Caller should check response.ok and use response.blob()
}

export function login(username, password) {
  return apiCall("/login", { method: "POST", body: { username, password }, auth: false });
}
export function listAgents() {
  return apiCall("/agent");
}
export function getAgent(agentId) {
  return apiCall(`/agent/${agentId}`);
}
export function createAgent(agentCard, documentIndexId = null, codeIndexId = null, knowledgeSpaceIds = null) {
  // Extract display_name from agentCard if present
  const { display_name, ...agentCardWithoutDisplayName } = agentCard;
  
  const body = { agent_card: agentCardWithoutDisplayName };
  if (display_name) body.display_name = display_name;
  if (documentIndexId) body.document_index_id = documentIndexId;
  if (Array.isArray(knowledgeSpaceIds) && knowledgeSpaceIds.length > 0) body.knowledge_space_ids = knowledgeSpaceIds;
  if (codeIndexId) body.code_index_id = codeIndexId;
  return apiCall("/agent", { method: "POST", body });
}
export function createAgentFromIntent({ intent, llm_name, display_name, tools_collection, max_tools, knowledge_space_ids }) {
  return apiCall("/agent/from-intent", {
    method: "POST",
    body: { intent, llm_name, display_name, tools_collection, max_tools, knowledge_space_ids }
  });
}
export function deleteAgent(agentId) {
  return apiCall(`/agent/${agentId}`, { method: "DELETE" });
}
export function cloneAgent(agentId, version = null) {
  const url = version ? `/agent/${agentId}/clone?version=${version}` : `/agent/${agentId}/clone`;
  return apiCall(url, { method: "POST" });
}
export function runAgent(agentId, input, openaiApiKey, files = null) {
  const body = { input };
  if (openaiApiKey) body.openai_api_key = openaiApiKey;
  if (files && files.length > 0) body.files = files;
  return apiCall(`/agent/${agentId}/run`, { method: "POST", body });
}
export function createAgentChatSession(agentId, body = {}) {
  return apiCall(`/agent/${agentId}/chat/sessions`, { method: "POST", body });
}
export function listAgentChatSessions(agentId) {
  return apiCall(`/agent/${agentId}/chat/sessions`);
}
export function listAgentChatMessages(agentId, sessionId) {
  return apiCall(`/agent/${agentId}/chat/sessions/${sessionId}/messages`);
}
export function sendAgentChatMessage(agentId, sessionId, content, files = null) {
  const body = { content };
  if (files && files.length > 0) body.files = files;
  return apiCall(`/agent/${agentId}/chat/sessions/${sessionId}/messages`, { method: "POST", body });
}
export function uploadAgentChatSessionFiles(agentId, sessionId, formData) {
  return fileUploadCall(
    `/agent/${agentId}/chat/sessions/${sessionId}/uploads`,
    { method: "POST", body: formData, auth: true },
  );
}
export function submitAgentOutcomeFeedback(agentId, sessionId, messageId, feedbackPayload) {
  return apiCall(
    `/agent/${agentId}/chat/sessions/${sessionId}/messages/${messageId}/feedback`,
    { method: "POST", body: feedbackPayload },
  );
}

export function listOAuthProviders() {
  return apiCall("/oauth/providers");
}

export function startOAuthConnect(payload) {
  return apiCall("/oauth/connect/start", { method: "POST", body: payload });
}
export function getCasperProfile() {
  return apiCall("/casper/profile");
}
export function getCasperOnboardingStatus() {
  return apiCall("/casper/onboarding/status");
}
export function resolveCasperIntent(payload) {
  return apiCall("/casper/resolve", { method: "POST", body: payload });
}
export function planCasperIntent(payload) {
  return apiCall("/casper/plan", { method: "POST", body: payload });
}
export function auditCasperPreviewEvent(payload) {
  return apiCall("/casper/preview/audit", { method: "POST", body: payload });
}
export function runCasperChat(payload) {
  return apiCall("/casper/chat", { method: "POST", body: payload });
}

export function composeCasperWorkflow(payload) {
  return apiCall("/casper/workflows/compose", { method: "POST", body: payload });
}

export function saveCasperWorkflow(payload) {
  return apiCall("/casper/workflows/save", { method: "POST", body: payload });
}

export function executeCasperWorkflow(payload) {
  return apiCall("/casper/workflows/execute", { method: "POST", body: payload });
}

export function getCasperWorkflowRunStatus(workflowId, version, runNumber) {
  return apiCall(
    `/casper/workflows/${encodeURIComponent(workflowId)}/version/${encodeURIComponent(version)}/runs/${encodeURIComponent(runNumber)}/status`,
  );
}

export function executeCasperEntity(payload) {
  return apiCall("/casper/execute", { method: "POST", body: payload });
}

export function uploadCasperSessionFiles(sessionId, formData) {
  return fileUploadCall(
    `/casper/sessions/${encodeURIComponent(sessionId)}/uploads`,
    { method: "POST", body: formData, auth: true },
  );
}

export function uploadCasperFiles(formData) {
  return fileUploadCall(
    "/casper/uploads",
    { method: "POST", body: formData, auth: true },
  );
}

export function listCasperSessions(limit = 50, runsPerSession = 50) {
  const params = new URLSearchParams({
    limit: String(limit),
    runs_per_session: String(runsPerSession),
  });
  return apiCall(`/casper/sessions?${params.toString()}`);
}

export function getCasperSession(sessionId) {
  return apiCall(`/casper/sessions/${encodeURIComponent(sessionId)}`);
}

export function submitCasperFeedback(payload) {
  return apiCall("/casper/feedback", { method: "POST", body: payload });
}
export function listAgentRuns(agentId, agentVersion) {
  return apiCall(`/agent/${agentId}/version/${agentVersion}/runs`);
}
export function getAgentRunPrompt(agentId, agentVersion, runNumber) {
  return apiCall(`/agent/${agentId}/version/${agentVersion}/runs/${runNumber}/prompt`);
}
export function getAgentRunResult(agentId, agentVersion, runNumber) {
  return apiCall(`/agent/${agentId}/version/${agentVersion}/runs/${runNumber}/result`);
}
export function getAgentRunArtifacts(agentId, agentVersion, runNumber) {
  return apiCall(`/agent/${agentId}/version/${agentVersion}/runs/${runNumber}/artifacts`);
}
export function renameAgentRun(agentId, agentVersion, runNumber, label) {
  return apiCall(`/agent/${agentId}/version/${agentVersion}/runs/${runNumber}/label`, {
    method: 'PUT',
    body: { label },
  });
}

/**
 * List all runs for a specific agent, optionally filtered by version, with pagination.
 * @param {string} agentId - Agent UUID
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.page_size - Items per page (default: 50, max: 200)
 * @param {number|null} options.version - Optional: filter by specific version
 * @returns {Promise} Response with { agent_id, total, page, page_size, runs: [...], version? }
 */
export function listAllAgentRuns(agentId, { page = 1, page_size = 50, version = null, triggered_by_workflow = null, workflow_id = null, workflow_version = null } = {}) {
  let url = `/agent/${agentId}/runs?page=${page}&page_size=${page_size}`;
  if (version !== null) {
    url += `&version=${version}`;
  }
  if (triggered_by_workflow !== null) {
    url += `&triggered_by_workflow=${triggered_by_workflow}`;
  }
  if (workflow_id !== null) {
    url += `&workflow_id=${encodeURIComponent(workflow_id)}`;
  }
  if (workflow_version !== null) {
    url += `&workflow_version=${workflow_version}`;
  }
  return apiCall(url);
}

// List all agent runs across all agents and versions, paginated. Optional: filter by agentId.
export function listAllRunsForAllAgents({ page = 1, page_size = 20, agent_id = null } = {}) {
  let url = `/agent/runs?page=${page}&page_size=${page_size}`;
  if (agent_id) {
    url += `&agent_id=${encodeURIComponent(agent_id)}`;
  }
  return apiCall(url);
}
export function getConfig() {
  return apiCall("/config");
}
export function saveConfig(config) {
  return apiCall("/config", { method: "POST", body: config });
}
export function getTenantBranding() {
  return apiCall("/config/branding", { auth: false });
}
export function saveTenantBranding(tenantBranding) {
  return apiCall("/config/branding", { method: "PUT", body: tenantBranding });
}
export async function uploadTenantBrandingAsset(kind, file) {
  const formData = new FormData();
  formData.append("file", file);
  return fileUploadCall(`/config/branding/assets?kind=${encodeURIComponent(kind)}`, {
    method: "POST",
    body: formData,
  });
}
export function getNeedlepathMetricsSummary(hours = 24) {
  return apiCall(`/needlepath/metrics/summary?hours=${encodeURIComponent(hours)}`);
}
export function listNeedlepathMetricRuns(limit = 50) {
  return apiCall(`/needlepath/metrics/runs?limit=${encodeURIComponent(limit)}`);
}
export function getMnemoLiveDashboard(hours = 24, limit = 80) {
  return apiCall(`/memory/live?hours=${encodeURIComponent(hours)}&limit=${encodeURIComponent(limit)}`);
}
export function materializeMnemoMemory(limit = 80) {
  return apiCall(`/memory/materialize?limit=${encodeURIComponent(limit)}`, { method: "POST" });
}
export function buildAgent(agentId) {
  return apiCall(`/agent/${agentId}/build`, { method: "POST" });
}
export function updateAgentStatus(agentId, status) {
  return apiCall(`/agent/${agentId}/status`, { method: "POST", body: { status } });
}
export function updateAgent(agentId, agentCard) {
  return apiCall(`/agent/${agentId}`, { method: "PUT", body: agentCard });
}
export function configureAgent(
  agentId,
  tools,
  version = null,
  display_name = null,
  llm = null,
  role = null,
  description = null,
  document_index_id = undefined,
  knowledge_space_ids = undefined,
) {
  // Use PUT instead of PATCH for better browser compatibility
  const body = { tools, version };
  
  // Add optional metadata fields if provided
  if (display_name !== null && display_name !== undefined) body.display_name = display_name;
  if (llm !== null && llm !== undefined) body.llm = llm;
  if (role !== null && role !== undefined) body.role = role;
  if (description !== null && description !== undefined) body.description = description;
  if (document_index_id !== undefined) body.document_index_id = document_index_id;
  if (knowledge_space_ids !== undefined) body.knowledge_space_ids = knowledge_space_ids;
  
  return apiCall(`/agent/${agentId}/configure`, { 
    method: "PUT", 
    body 
  });
}
export function attachAgentKnowledgeIndex(agentId, indexId, version = null) {
  const body = { index_id: indexId };
  if (version !== null && version !== undefined) body.version = version;
  return apiCall(`/agent/${agentId}/knowledge-indexes`, { method: "POST", body });
}

export function listAgentKnowledgeSpaces(agentId, version = null) {
  const suffix = version !== null && version !== undefined ? `?version=${encodeURIComponent(version)}` : "";
  return apiCall(`/agent/${agentId}/knowledge-spaces${suffix}`);
}

export function attachAgentKnowledgeSpaces(agentId, knowledgeSpaceIds, version = null) {
  const body = { knowledge_space_ids: Array.isArray(knowledgeSpaceIds) ? knowledgeSpaceIds : [knowledgeSpaceIds] };
  if (version !== null && version !== undefined) body.version = version;
  return apiCall(`/agent/${agentId}/knowledge-spaces`, { method: "POST", body });
}

export function listKnowledgeSpaceSources(spaceId, version = null) {
  const suffix = version !== null && version !== undefined ? `?version=${encodeURIComponent(version)}` : "";
  return apiCall(`/knowledge-spaces/${encodeURIComponent(spaceId)}/sources${suffix}`);
}

export function createKnowledgeSpaceSource(spaceId, sourcePayload) {
  return apiCall(`/knowledge-spaces/${encodeURIComponent(spaceId)}/sources`, {
    method: "POST",
    body: sourcePayload,
  });
}

function makeFileMultipartAuthOptions({ method = "GET", body = null, auth = true } = {}) {
  const token = getAuthToken();
  const headers = token ? { "Authorization": `Bearer ${token}` } : {};
  const opts = { method, body, headers };
  return opts;
}
async function fileUploadCall(path, { method = "POST", body, auth = true } = {}) {
  markActivity();
  const res = await fetch(`${API_BASE}${path}`, makeFileMultipartAuthOptions({ method, body, auth }));
  if (res.status === 401) {
    removeAuthToken();
    window.location.replace("/login");
    return;
  }
  if (!res.ok) throw new Error(await res.text());
  return await res.json();
}

export function listAgentSkills(agentId, version = null) {
  const suffix = version !== null && version !== undefined ? `?version=${encodeURIComponent(version)}` : "";
  return apiCall(`/agent/${agentId}/skills${suffix}`);
}

export function uploadAgentSkill(agentId, formData) {
  return fileUploadCall(`/agent/${agentId}/skills`, { method: "POST", body: formData, auth: true });
}

export function deleteAgentSkill(agentId, skillId) {
  return apiCall(`/agent/${agentId}/skills/${skillId}`, { method: "DELETE" });
}

export function uploadFiles(formData) {
  return fileUploadCall("/uploadDocuments", { method: "POST", body: formData, auth: true });
}
export function listCollectionFiles(uploadId, skip = 0, limit = 100) {
  return apiCall(`/collections/${uploadId}/files?skip=${skip}&limit=${limit}`);
}
export async function downloadCollectionFile(uploadId, filePath) {
  markActivity();
  const token = getAuthToken();
  const url = `${API_BASE}/collections/${uploadId}/download?path=${encodeURIComponent(filePath)}`;
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  if (!response.ok) {
    throw new Error('Download failed');
  }
  return response.blob();
}
export function createIndex({ name, description, path, type }) {
  return apiCall("/index", { method: "POST", body: { name, description, path, type } });
}
export function listIndexes(type) {
  const url = type ? `/index?type_=${type}` : "/index";
  return apiCall(url);
}
export function getIndexDetail(indexId) {
  return apiCall(`/index/${indexId}`);
}
export function getIndexHealth(indexId) {
  return apiCall(`/index/${indexId}/health`);
}
export function listIndexAssets(indexId, skip = 0, limit = 100) {
  return apiCall(`/index/${indexId}/assets?skip=${skip}&limit=${limit}`);
}
export function listIndexRuns(indexId, limit = 20) {
  return apiCall(`/index/${indexId}/runs?limit=${limit}`);
}
export function testIndexQuery(indexId, { query, k = 8, summarize = false, include_context = true }) {
  return apiCall(`/index/${indexId}/query-test`, {
    method: "POST",
    body: { query, k, summarize, include_context },
  });
}
export function updateIndex(indexId, updateData) {
  return apiCall(`/index/${indexId}`, { method: "PUT", body: updateData });
}
export function deleteIndex(indexId) {
  return apiCall(`/index/${indexId}`, { method: "DELETE" });
}
export function listAuditLogs({ username, action, status, targetId, startTime, endTime, page = 1, pageSize = 50 } = {}) {
  const params = new URLSearchParams();
  if (username) params.append("username", username);
  if (action) params.append("action", action);
  if (status) params.append("status", status);
  if (targetId) params.append("target_id", targetId);
  if (startTime) params.append("start_time", startTime);
  if (endTime) params.append("end_time", endTime);
  params.append("page", page);
  params.append("page_size", pageSize);
  return apiCall(`/audit_logs?${params.toString()}`);
}
export function getProfile() {
  return apiCall("/profile");
}

export function changePassword(newPassword, oldPassword = null) {
  const payload = { new_password: newPassword };
  if (oldPassword) {
    payload.old_password = oldPassword;
  }
  return apiCall("/profile/password", { method: "PUT", body: payload });
}

// Prompt Enhancement APIs
export function enhancePrompt({ role, description, input_prompt, tools, mode = "zero_shot", model = "gpt-4o", llm_overrides }) {
  const body = { role, description, input_prompt, tools, mode, model, llm_overrides };
  return apiCall("/enhance_prompt", { method: "POST", body });
}

/**
 * Poll agent run result endpoint for final output.
 * @param {string} agentId - Agent ID
 * @param {number|string} version - Agent version
 * @param {number|string} run - Run number
 * @returns {Promise<Object>} - JSON response {status, output}
 */
export function getAgentRunResultFromUrl(url) {
  return apiCall(url);
}

export async function fetchAgentRunStreamingResult(streamingUrl) {
  markActivity();
  const token = getAuthToken();
  const headers = token ? { "Authorization": `Bearer ${token}` } : {};
  const res = await fetch(`${API_BASE}${streamingUrl.startsWith("/") ? streamingUrl : "/" + streamingUrl}`, { headers });
  if (!res.ok) throw new Error(await res.text());
  // Read the stream as text
  const reader = res.body.getReader();
  let result = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    result += new TextDecoder().decode(value);
  }
  return result;
}

/**
 * Stream agent run result using SSE (EventSource) with typewriter effect.
 * @param {string} streamingUrl - The SSE endpoint URL (relative path).
 * @param {(chunk: string) => void} onMessage - Callback for displaying output (receives progressive text).
 * @param {(error: any) => void} onError - Callback for error.
 * @param {Object} options - Configuration options.
 * @param {number|function} options.typewriterSpeed - Milliseconds per character (0 = instant, 10 = fast, 20 = normal, 50 = slow), or a function that returns the speed.
 * @param {function} [options.onAnimationEnd] - Callback fired after SSE closes and the typewriter animation completes.
 * @returns {Object} - Object with EventSource instance and cleanup function.
 */
export function streamAgentRunResult(streamingUrl, onMessage, onError, options = {}) {
  const { typewriterSpeed = 20, onAnimationEnd } = options; // Default: 20ms per char (50 chars/sec)

  markActivity();

  // Support both static number and dynamic function for speed
  let currentSpeed = typeof typewriterSpeed === 'function' ? typewriterSpeed() : typewriterSpeed;
  const getSpeed = typeof typewriterSpeed === 'function' ? typewriterSpeed : () => currentSpeed;

  const token = getAuthToken();
  // Build full URL
  const url = `${API_BASE}${streamingUrl.startsWith("/") ? streamingUrl : "/" + streamingUrl}`;
  // Add token as query param (SSE does not support custom headers)
  const fullUrl = token ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : url;

  const es = new window.EventSource(fullUrl);

  // Typewriter effect state
  let buffer = "";
  let displayedLength = 0;
  let animationTimeout = null;
  let sseClosed = false;
  let normalCompletion = false;
  let completionFinalized = false;

  const finishOnce = () => {
    if (completionFinalized) return;
    completionFinalized = true;
    if (typeof onAnimationEnd === 'function') {
      onAnimationEnd();
    }
  };

  // Typewriter animation function
  const animateTypewriter = () => {
    if (displayedLength < buffer.length) {
      displayedLength++;
      if (onMessage) onMessage(buffer.substring(0, displayedLength));
      const speed = getSpeed(); // Get current speed dynamically
      animationTimeout = setTimeout(animateTypewriter, speed);
    } else {
      // Clear timeout when animation catches up
      animationTimeout = null;
      // If SSE is closed and animation is now done, call onAnimationEnd
      if (sseClosed) {
        finishOnce();
      }
    }
  };

  es.onmessage = (event) => {
    // Redis-backed chat streams send JSON events; legacy run.out streams send raw text.
    let chunk = event.data;
    try {
      const parsed = JSON.parse(event.data);
      if (parsed && typeof parsed === "object") {
        if (parsed.type === "token") {
          chunk = parsed.content || "";
        } else if (parsed.type === "final") {
          buffer = parsed.content || "";
          chunk = "";
          displayedLength = Math.min(displayedLength, buffer.length);
        } else if (parsed.type === "error") {
          if (onError) onError(parsed.content || parsed);
          return;
        } else if (parsed.type === "done") {
          normalCompletion = true;
          sseClosed = true;
          es.close();
          if (animationTimeout) {
            return;
          }
          finishOnce();
          return;
        } else {
          chunk = "";
        }
      }
    } catch (_err) {
      // Raw legacy stream chunk.
    }

    // Add new chunk to buffer
    buffer += chunk;

    const speed = getSpeed();
    // If typewriter speed is 0 (instant), show all immediately
    if (speed === 0) {
      if (animationTimeout) {
        clearTimeout(animationTimeout);
        animationTimeout = null;
      }
      displayedLength = buffer.length;
      if (onMessage) onMessage(buffer);
      // If SSE is closed and animation is now done, call onAnimationEnd
      if (sseClosed) {
        finishOnce();
      }
    } else {
      // Start animation if not already running
      if (!animationTimeout) {
        animateTypewriter();
      }
    }
  };

  es.onerror = (err) => {
    sseClosed = true;
    if (normalCompletion) {
      es.close();
      if (!animationTimeout) {
        finishOnce();
      }
      return;
    }
    if (animationTimeout) clearTimeout(animationTimeout);
    // Only call onError if not all output has been displayed
    // If buffer is empty or animation not finished, treat as error
    if (buffer.length === 0 || displayedLength < buffer.length) {
      if (onError) onError(err);
      // If animation not finished, continue animating until done
      if (displayedLength < buffer.length) {
        animateTypewriter();
      } else {
        // Animation already done, call onAnimationEnd
        finishOnce();
      }
    } else {
      // Otherwise, treat as normal close (do not call onError)
      finishOnce();
    }
    es.close();
  };

  // Extended cleanup function
  const cleanup = () => {
    if (animationTimeout) clearTimeout(animationTimeout);
    es.close();
  };

  // Method to update speed on the fly
  const updateSpeed = (newSpeed) => {
    currentSpeed = newSpeed;

    // If animation is running and speed changed, restart with new speed
    if (animationTimeout && displayedLength < buffer.length) {
      clearTimeout(animationTimeout);
      animationTimeout = null;

      // If new speed is 0 (instant), show all immediately
      if (newSpeed === 0) {
        displayedLength = buffer.length;
        if (onMessage) onMessage(buffer);
        // If SSE is closed and animation is now done, call onAnimationEnd
        if (sseClosed && typeof onAnimationEnd === 'function') {
          onAnimationEnd();
        }
      } else {
        // Restart animation with new speed
        animateTypewriter();
      }
    }
  };

  // Return extended EventSource with cleanup
  return {
    eventSource: es,
    cleanup,
    updateSpeed, // Expose method to change speed without reconnecting
    // Expose skip animation method (for "show all" button)
    skipAnimation: () => {
      if (animationTimeout) clearTimeout(animationTimeout);
      displayedLength = buffer.length;
      if (onMessage && buffer) onMessage(buffer);
      // If SSE is closed and animation is now done, call onAnimationEnd
      if (sseClosed && typeof onAnimationEnd === 'function') {
        onAnimationEnd();
      }
    }
  };
}

export function registerUser({ username, email, password, role, role_ids = null }) {
  const body = { username, email, password, role };
  if (Array.isArray(role_ids) && role_ids.length > 0) {
    body.role_ids = role_ids;
  }
  return apiCall("/register", {
    method: "POST",
    body,
    auth: true,
  });
}

// -------- Pinned Prompts API functions ----------
export function createPinnedPromptForAgent(agentId, { prompt_text, is_public = false }) {
  return apiCall(`/agent/${agentId}/pinned_prompt`, {
    method: "POST",
    body: { 
      entity_type: "agent",
      entity_id: agentId,
      prompt_text, 
      is_public 
    }
  });
}

export function createPinnedPromptForWorkflow(workflowId, { prompt_text, is_public = false }) {
  return apiCall(`/workflow/${workflowId}/pinned_prompt`, {
    method: "POST",
    body: { prompt_text, is_public }
  });
}

export function getPinnedPromptForAgent(agentId, promptId) {
  return apiCall(`/agent/${agentId}/pinned_prompt/${promptId}`);
}

export function getPinnedPromptForWorkflow(workflowId, promptId) {
  return apiCall(`/workflow/${workflowId}/pinned_prompt/${promptId}`);
}

export function listPinnedPromptsForAgent(agentId) {
  return apiCall(`/agent/${agentId}/pinned_prompt`);
}

export function listPinnedPromptsForWorkflow(workflowId) {
  return apiCall(`/workflow/${workflowId}/pinned_prompt`);
}

export function updatePinnedPromptForAgent(agentId, promptId, { prompt_text, is_public }) {
  const body = {};
  if (prompt_text !== undefined) body.prompt_text = prompt_text;
  if (is_public !== undefined) body.is_public = is_public;
  
  return apiCall(`/agent/${agentId}/pinned_prompt/${promptId}`, {
    method: "PUT",
    body
  });
}

export function updatePinnedPromptForWorkflow(workflowId, promptId, { prompt_text, is_public }) {
  const body = {};
  if (prompt_text !== undefined) body.prompt_text = prompt_text;
  if (is_public !== undefined) body.is_public = is_public;
  
  return apiCall(`/workflow/${workflowId}/pinned_prompt/${promptId}`, {
    method: "PUT",
    body
  });
}

export function deletePinnedPromptForAgent(agentId, promptId) {
  return apiCall(`/agent/${agentId}/pinned_prompt/${promptId}`, {
    method: "DELETE"
  });
}

export function deletePinnedPromptForWorkflow(workflowId, promptId) {
  return apiCall(`/workflow/${workflowId}/pinned_prompt/${promptId}`, {
    method: "DELETE"
  });
}

// End ----- Pinned Prompts API functions -------

// -------- Tools Catalog API functions ----------
export function hydrateTools(force = false) {
  return apiCall(`/tools/hydrate?force=${force}`, { method: "POST" });
}

export function listTools({ category, status, is_public, tags, created_by } = {}) {
  const params = new URLSearchParams();
  if (category) params.append("category", category);
  if (status) params.append("status", status);
  if (is_public !== undefined) params.append("is_public", is_public);
  if (tags) params.append("tags", tags);
  if (created_by) params.append("created_by", created_by);
  return apiCall(`/tools?${params.toString()}`);
}

/**
 * Get the current version of a tool by ID
 * @param {string} toolId - Tool UUID
 * @returns {Promise} Tool data (current version)
 */
export function getTool(toolId) {
  return apiCall(`/tools/${toolId}`);
}

/**
 * Get a specific version of a tool by ID
 * @param {string} toolId - Tool UUID
 * @param {number} version - Integer version (1, 2, 3, etc.)
 * @returns {Promise} Tool data (specific version)
 */
export function getToolVersion(toolId, version) {
  return apiCall(`/tools/${toolId}/version/${version}`);
}

/**
 * Get tool(s) by name, optionally filtered by version
 * @param {string} toolName - Tool name
 * @param {number|null} version - Optional integer version
 * @returns {Promise} Tool data array
 */
export function getToolByName(toolName, version = null) {
  const params = version !== null ? `?version=${version}` : '';
  return apiCall(`/tools/by-name/${toolName}${params}`);
}

export function createTool(toolCard) {
  return apiCall("/tools", { method: "POST", body: toolCard });
}

/**
 * Update the current version of a tool
 * @param {string} toolId - Tool UUID
 * @param {object} toolCard - Tool update data
 * @returns {Promise} Updated tool data
 */
export function updateTool(toolId, toolCard) {
  return apiCall(`/tools/${toolId}`, { method: "PUT", body: toolCard });
}

/**
 * Update a specific version of a tool
 * @param {string} toolId - Tool UUID
 * @param {number} version - Integer version (1, 2, 3, etc.)
 * @param {object} toolCard - Tool update data
 * @returns {Promise} Updated tool data
 */
export function updateToolVersion(toolId, version, toolCard) {
  return apiCall(`/tools/${toolId}/version/${version}`, { method: "PUT", body: toolCard });
}

/**
 * Delete the current version of a tool
 * @param {string} toolId - Tool UUID
 * @returns {Promise} Deletion confirmation
 */
export function deleteTool(toolId) {
  return apiCall(`/tools/${toolId}`, { method: "DELETE" });
}

/**
 * Delete a specific version of a tool
 * @param {string} toolId - Tool UUID
 * @param {number} version - Integer version (1, 2, 3, etc.)
 * @returns {Promise} Deletion confirmation
 */
export function deleteToolVersion(toolId, version) {
  return apiCall(`/tools/${toolId}/version/${version}`, { method: "DELETE" });
}
// End ----- Tools Catalog API functions -------


// -------- Workflow API functions ----------

export function generateWorkflow(intent) {
  return apiCall("/workflow/generate", {
    method: "POST",
    body: { intent }
  });
}

export function refineWorkflow(originalIntent, refinement, currentDag, agentCatalogTopK = 20, intentHistory = null) {
  return apiCall("/workflow/refine", {
    method: "POST",
    body: {
      original_intent: originalIntent,
      refinement,
      current_dag: currentDag,
      agent_catalog_top_k: agentCatalogTopK,
      ...(intentHistory ? { intent_history: intentHistory } : {}),
    }
  });
}

export function createWorkflow(name, dagDefinition, intentHistory = null) {
  return apiCall("/workflow", {
    method: "POST",
    body: { name, dag_definition: dagDefinition, ...(intentHistory ? { intent_history: intentHistory } : {}) }
  });
}

export function updateWorkflow(workflowId, name, dagDefinition) {
  return apiCall(`/workflow/${workflowId}`, {
    method: "PUT",
    body: { name, dag_definition: dagDefinition }
  });
}

/**
 * Refine a saved workflow in-place using a natural language refinement instruction.
 * Calls PUT /workflow/{workflowId}/refine — saves as a new version immediately.
 * No original intent required; the backend derives context from the workflow itself.
 */
export function refineWorkflowInPlace(workflowId, refinement, agentCatalogTopK = 20) {
  return apiCall(`/workflow/${workflowId}/refine`, {
    method: "PUT",
    body: { refinement, agent_catalog_top_k: agentCatalogTopK }
  });
}

export function getWorkflow(workflowId, version = null) {
  const url = version ? `/workflow/${workflowId}?version=${version}` : `/workflow/${workflowId}`;
  return apiCall(url);
}

export function runWorkflow(workflowId, input) {
  return apiCall(`/workflow/${workflowId}/run`, {
    method: "POST",
    body: { input }
  });
}

export function listWorkflows() {
  return apiCall("/workflow");
}

export function deleteWorkflow(workflowId) {
  return apiCall(`/workflow/${workflowId}`, { method: "DELETE" });
}

export function listWorkflowRuns(workflowId, { limit = 50, offset = 0 } = {}) {
  return apiCall(`/workflow/${workflowId}/runs?limit=${limit}&offset=${offset}`);
}

export function getWorkflowRunDetails(workflowId, version, runNumber) {
  return apiCall(`/workflow/${workflowId}/version/${version}/runs/${runNumber}`);
}

// Schedule configuration
export function configureWorkflowSchedule(workflowId, { cron_expression, enabled = true }) {
  return apiCall(`/workflow/${workflowId}/schedule/config`, {
    method: "PUT",
    body: { cron_expression, enabled }
  });
}

// Webhook configuration
/**
 * Configure webhook for a workflow
 * @param {string} workflowId - Workflow UUID
 * @param {Object} config - Webhook configuration
 * @param {boolean} config.enabled - Whether webhook is enabled
 * @param {string} config.adapter_type - Adapter type: "slack", "zendesk", "generic"
 * @param {Object} config.secrets_ref - Authentication secrets (key-value pairs)
 * 
 * @example
 * // Slack webhook
 * configureWorkflowWebhook(workflowId, {
 *   enabled: true,
 *   adapter_type: "slack",
 *   secrets_ref: { signing_secret: "abc123..." }
 * })
 * 
 * // Zendesk with API key
 * configureWorkflowWebhook(workflowId, {
 *   enabled: true,
 *   adapter_type: "zendesk",
 *   secrets_ref: { api_key: "xyz789..." }
 * })
 * 
 * // Generic with Bearer token
 * configureWorkflowWebhook(workflowId, {
 *   enabled: true,
 *   adapter_type: "generic",
 *   secrets_ref: { webhook_token: "token123..." }
 * })
 */
export function configureWorkflowWebhook(workflowId, { enabled = true, adapter_type, secrets_ref }) {
  return apiCall(`/workflow/${workflowId}/webhook/config`, {
    method: "PUT",
    body: { enabled, adapter_type, secrets_ref }
  });
}

export function getWorkflowScheduleConfig(workflowId) {
  return apiCall(`/workflow/${workflowId}/schedule/config`);
}

export function deleteWorkflowScheduleConfig(workflowId) {
  return apiCall(`/workflow/${workflowId}/schedule/config`, { method: "DELETE" });
}

// Webhook configuration
export function deleteWorkflowWebhookConfig(workflowId) {
  return apiCall(`/workflow/${workflowId}/webhook/config`, { method: "DELETE" });
}

export function getWorkflowWebhookConfig(workflowId) {
  return apiCall(`/workflow/${workflowId}/webhook/config`);
}

// Resume a paused workflow run (after HITL approval or wait timer)
export function resumeWorkflowRun(workflowId, version, runNumber, input = null) {
  const body = input ? { input } : {};
  return apiCall(`/workflow/${workflowId}/version/${version}/runs/${runNumber}/resume`, {
    method: "POST",
    body,
  });
}

// List human-in-the-loop approval requests (with optional status filter)
export function listApprovals(status = null) {
  const params = status ? `?status=${status}` : '';
  return apiCall(`/workflow/approvals${params}`);
}

// List pending approvals (backward compatibility alias)
export function listPendingApprovals() {
  return listApprovals('pending');
}

// Get approval request details
export function getApprovalDetail(approvalId) {
  return apiCall(`/workflow/approvals/${approvalId}`);
}

// Respond to an approval request (approve/reject with optional data)
export function respondToApproval(approvalId, responseData) {
  return apiCall(`/workflow/approvals/${approvalId}/respond`, {
    method: "POST",
    body: responseData,
  });
}

// End ----- Workflow API functions -------


// ----- Key Manager API functions -------

/**
 * Create a new credential.
 * @param {Object} keyData - Key creation data
 * @param {string} keyData.name - Credential name (required)
 * @param {string} [keyData.value] - API key value, or OAuth access-token shorthand
 * @param {string} [keyData.description] - Key description (optional)
 * @param {string} [keyData.provider] - Provider name (optional, e.g., "OpenAI", "Anthropic")
 * @param {string} [keyData.key_type] - Key type (default: "api_key")
 * @param {boolean} [keyData.is_private] - Private key (default: true)
 * @returns {Promise} Created key object
 */
export function createKey(keyData) {
  return apiCall("/keys", { method: "POST", body: keyData });
}

/**
 * List all credentials (public + user's private credentials).
 * @param {Object} [filters] - Optional filters
 * @param {string} [filters.key_type] - Filter by key type
 * @param {boolean} [filters.is_private] - Filter by visibility
 * @returns {Promise} Object with keys array
 */
export function listKeys(filters = {}) {
  const params = new URLSearchParams(filters).toString();
  const path = params ? `/keys?${params}` : "/keys";
  return apiCall(path);
}

/**
 * Get a specific credential by ID.
 * @param {string} keyId - Key UUID
 * @returns {Promise} Key object
 */
export function getKey(keyId) {
  return apiCall(`/keys/${keyId}`);
}

/**
 * Delete a credential.
 * @param {string} keyId - Key UUID
 * @returns {Promise} Deletion confirmation
 */
export function deleteKey(keyId) {
  return apiCall(`/keys/${keyId}`, { method: "DELETE" });
}

/**
 * Resolve a credential's secret fragments (admin-only or key owner).
 * @param {string} keyId - Key UUID
 * @returns {Promise} Object with resolved key value
 */
export function resolveKey(keyId) {
  return apiCall(`/keys/${keyId}/resolve`, { method: "POST" });
}

/**
 * Associate a key with an entity (agent/tool/workflow)
 * @param {Object} association - Association data
 * @param {string} association.key_id - Key UUID
 * @param {string} association.entity_type - Entity type ("agent", "tool", "workflow")
 * @param {string} association.entity_id - Entity UUID
 * @param {number} [association.entity_version] - Entity version (optional)
 * @returns {Promise} Created association object
 */
export function createKeyAssociation(association) {
  return apiCall("/keys/associations", { method: "POST", body: association });
}

/**
 * List key associations for an entity or key
 * @param {Object} filters - Filter parameters
 * @param {string} [filters.entity_type] - Entity type
 * @param {string} [filters.entity_id] - Entity UUID
 * @param {string} [filters.key_id] - Key UUID
 * @returns {Promise} Object with associations array
 */
export function listKeyAssociations(filters = {}) {
  const params = new URLSearchParams(filters).toString();
  const path = params ? `/keys/associations?${params}` : "/keys/associations";
  return apiCall(path);
}

/**
 * Delete a key association
 * @param {string} entityType - Entity type ("agent", "tool", "workflow")
 * @param {string} entityId - Entity UUID
 * @param {string} keyId - Key UUID
 * @returns {Promise} Deletion confirmation
 */
export function deleteKeyAssociation(entityType, entityId, keyId) {
  return apiCall(`/keys/associations/${entityType}/${entityId}/${keyId}`, { method: "DELETE" });
}

/**
 * Create multiple key associations in batch
 * @param {Array} associations - Array of association objects
 * @returns {Promise} Batch creation result
 */
export function createKeyAssociationsBatch(associations) {
  return apiCall("/keys/associations/batch", { method: "POST", body: { associations } });
}

/**
 * Check if required keys exist for an entity
 * @param {Object} checkData - Check data
 * @param {string} checkData.entity_type - Entity type
 * @param {string} checkData.entity_id - Entity UUID
 * @param {number} [checkData.entity_version] - Entity version
 * @param {Array<string>} checkData.required_keys - Array of required key names
 * @returns {Promise} Check result with missing keys
 */
export function checkKeys(checkData) {
  return apiCall("/keys/check", { method: "POST", body: checkData });
}

/**
 * Get key rotation history
 * @param {string} keyId - Key UUID
 * @returns {Promise} Object with history array
 */
export function getKeyHistory(keyId) {
  return apiCall(`/keys/${keyId}/history`);
}

// End ----- Key Manager API functions -------

// ----- User Management API functions -------

/**
 * List all users with pagination
 * @param {number} [offset=0] - Number of users to skip (default: 0)
 * @param {number} [limit=50] - Number of users to return (default: 50)
 * @returns {Promise} Object with users array and pagination info
 */
export function listUsers(offset = 0, limit = 50) {
  return apiCall(`/users?offset=${offset}&limit=${limit}`);
}

/**
 * Delete a user by ID
 * @param {string|number} userId - User ID, UUID, or username
 * @returns {Promise} Deletion confirmation
 */
export function deleteUser(userId) {
  return apiCall(`/users/${userId}`, { method: "DELETE" });
}

/**
 * Set or reset a user's password
 * @param {string|number} userId - User ID, UUID, or username
 * @param {string} newPassword - New password value
 * @returns {Promise} Password update confirmation
 */
export function setUserPassword(userId, newPassword) {
  return apiCall(`/users/${userId}/password`, {
    method: "PUT",
    body: { new_password: newPassword }
  });
}

export function listPermissions() {
  return apiCall("/permissions");
}

export function listRoleTemplates() {
  return apiCall("/role-templates");
}

export function listRoles() {
  return apiCall("/roles");
}

export function createRole(payload) {
  return apiCall("/roles", {
    method: "POST",
    body: payload,
  });
}

export function updateRole(roleId, payload) {
  return apiCall(`/roles/${roleId}`, {
    method: "PUT",
    body: payload,
  });
}

export function deleteRole(roleId) {
  return apiCall(`/roles/${roleId}`, { method: "DELETE" });
}

/**
 * Get roles assigned to a user.
 * @param {string|number} userId - User ID, UUID, or username
 * @returns {Promise} User role summaries
 */
export function getUserRoles(userId) {
  return apiCall(`/users/${userId}/roles`);
}

/**
 * Set roles assigned to a user.
 * @param {string|number} userId - User ID, UUID, or username
 * @param {string[]} roleIds - Role IDs to assign
 * @returns {Promise} User role summaries
 */
export function setUserRoles(userId, roleIds) {
  return apiCall(`/users/${userId}/roles`, {
    method: "PUT",
    body: { role_ids: roleIds },
  });
}

// End ----- User Management API functions -------
